import React, { useEffect, useState, useMemo, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import GlobalStyle from "./GlobalStyle";
import ReactModal from "react-modal";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { FaSearch, FaBars, FaTimes, FaEdit, FaTrash, FaTruck, FaPlus, FaFilePdf, FaMapMarkedAlt } from "react-icons/fa";
import L from "leaflet";
import marker2x from "leaflet/dist/images/marker-icon-2x.png";
import marker from "leaflet/dist/images/marker-icon.png";
import shadow from "leaflet/dist/images/marker-shadow.png";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.Default.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: marker2x, iconUrl: marker, shadowUrl: shadow });
ReactModal.setAppElement("#root");

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  bg: "#f8fafc",
  card: "#ffffff",
  primary: "#6366f1",
  primaryDark: "#4f46e5",
  primaryLight: "#eef2ff",
  danger: "#ef4444",
  dangerLight: "#fef2f2",
  warn: "#f59e0b",
  success: "#10b981",
  successLight: "#ecfdf5",
  text: "#0f172a",
  textSec: "#475569",
  muted: "#94a3b8",
  border: "#e2e8f0",
  borderHover: "#cbd5e1",
  shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowMd: "0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -1px rgba(0,0,0,0.04)",
  shadowLg: "0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -2px rgba(0,0,0,0.04)",
};

const Fonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
`;

const fadeIn = keyframes`from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); }`;

// ─── Layout ───────────────────────────────────────────────────────────────────
const Page = styled.div`
  font-family: 'Inter', 'Segoe UI', sans-serif;
  background: ${C.bg};
  min-height: 100vh;
  padding: 32px 28px;
  color: ${C.text};
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 12px;
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: ${C.text};
  letter-spacing: -0.3px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 24px;
  align-items: start;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// ─── Card ─────────────────────────────────────────────────────────────────────
const Card = styled.div`
  background: ${C.card};
  border: 1px solid ${C.border};
  border-radius: 14px;
  padding: 24px;
  box-shadow: ${C.shadow};
  animation: ${fadeIn} 0.2s ease;
`;

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${C.text};
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid ${C.border};
  display: flex;
  align-items: center;
  gap: 8px;
`;

// ─── Form ─────────────────────────────────────────────────────────────────────
const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 18px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${C.textSec};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 5px;
  display: block;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid ${C.border};
  border-radius: 8px;
  font-size: 14px;
  color: ${C.text};
  background: ${C.bg};
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: inherit;
  &:focus {
    border-color: ${C.primary};
    box-shadow: 0 0 0 3px ${C.primaryLight};
    background: #fff;
  }
  &::placeholder { color: ${C.muted}; }
`;

const ColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ColorPreview = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ color }) => color};
  border: 2px solid ${C.border};
  flex-shrink: 0;
`;

const ColorInput = styled.input`
  width: 48px;
  height: 36px;
  border: 1.5px solid ${C.border};
  border-radius: 8px;
  cursor: pointer;
  padding: 2px;
  background: #fff;
  &::-webkit-color-swatch-wrapper { padding: 2px; }
  &::-webkit-color-swatch { border-radius: 6px; border: none; }
`;

const ColorHint = styled.span`
  font-size: 12px;
  color: ${C.muted};
`;

const FormActions = styled.div`
  display: flex;
  gap: 8px;
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: ${({ sm }) => sm ? "6px 12px" : "9px 16px"};
  font-size: ${({ sm }) => sm ? "13px" : "14px"};
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  font-family: inherit;

  ${({ variant }) => variant === "primary" && `
    background: #6366f1;
    color: #fff;
    &:hover { background: #4f46e5; }
  `}
  ${({ variant }) => variant === "danger" && `
    background: #fef2f2;
    color: #ef4444;
    &:hover { background: #fde8e8; }
  `}
  ${({ variant }) => variant === "ghost" && `
    background: transparent;
    color: #475569;
    border: 1.5px solid #e2e8f0;
    &:hover { background: #f8fafc; border-color: #cbd5e1; color: #0f172a; }
  `}
  ${({ variant }) => variant === "success" && `
    background: #ecfdf5;
    color: #10b981;
    &:hover { background: #d1fae5; }
  `}
  ${({ variant }) => (!variant || variant === "default") && `
    background: #eef2ff;
    color: #6366f1;
    &:hover { background: #e0e7ff; }
  `}
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ─── Driver List ──────────────────────────────────────────────────────────────
const DriverList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DriverCard = styled.div`
  background: ${C.card};
  border: 1.5px solid ${({ editing }) => editing ? C.primary : C.border};
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: border-color 0.15s, box-shadow 0.15s;
  animation: ${fadeIn} 0.18s ease;
  &:hover {
    border-color: ${C.borderHover};
    box-shadow: ${C.shadowMd};
  }
`;

const DriverAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  flex-shrink: 0;
`;

const DriverInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DriverName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${C.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DriverVehicle = styled.div`
  font-size: 12px;
  color: ${C.muted};
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const DriverActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: all 0.15s;
  ${({ variant }) => variant === "edit" && `
    background: #eef2ff;
    color: #6366f1;
    &:hover { background: #e0e7ff; }
  `}
  ${({ variant }) => variant === "delete" && `
    background: #fef2f2;
    color: #ef4444;
    &:hover { background: #fde8e8; }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${C.muted};
  font-size: 14px;
  line-height: 1.6;
`;

// ─── Modal helpers ────────────────────────────────────────────────────────────
const modalOverlay = {
  backgroundColor: "rgba(15,23,42,0.45)",
  backdropFilter: "blur(2px)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const buildModalContent = (w = "560px") => ({
  position: "relative",
  inset: "auto",
  maxWidth: w,
  width: "calc(100vw - 32px)",
  background: "#fff",
  borderRadius: "16px",
  padding: "28px",
  border: "none",
  boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
  fontFamily: "'Inter','Segoe UI',sans-serif",
  maxHeight: "90vh",
  overflowY: "auto",
});

const ModalTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${C.text};
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid ${C.border};
  display: flex;
  align-items: center;
  gap: 9px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${C.border};
  flex-wrap: wrap;
`;

const FormRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const FormLabel = styled.label`
  flex: 0 0 70px;
  font-size: 13px;
  font-weight: 600;
  color: ${C.textSec};
`;

const ModalInput = styled.input`
  flex: 1;
  padding: 9px 12px;
  border: 1.5px solid ${C.border};
  border-radius: 8px;
  font-size: 14px;
  color: ${C.text};
  background: ${C.bg};
  outline: none;
  font-family: inherit;
  &:focus {
    border-color: ${C.primary};
    box-shadow: 0 0 0 3px ${C.primaryLight};
    background: #fff;
  }
`;

const ModalSelect = styled.select`
  flex: 1;
  padding: 9px 12px;
  border: 1.5px solid ${C.border};
  border-radius: 8px;
  font-size: 14px;
  color: ${C.text};
  background: ${C.bg};
  outline: none;
  font-family: inherit;
  &:focus {
    border-color: ${C.primary};
    box-shadow: 0 0 0 3px ${C.primaryLight};
    background: #fff;
  }
`;

const DragItem = styled.li`
  background: ${C.bg};
  border: 1.5px solid ${C.border};
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  cursor: grab;
  transition: box-shadow 0.15s;
  &:hover { box-shadow: ${C.shadowMd}; border-color: ${C.borderHover}; }
`;

// ─── Component ────────────────────────────────────────────────────────────────
function Autisti() {
  const [autisti, setAutisti]             = useState([]);
  const [nome, setNome]                   = useState("");
  const [veicolo, setVeicolo]             = useState("");
  const [colore, setColore]               = useState("#6366f1");
  const [editingNome, setEditingNome]     = useState(null);
  const [showGestione, setShowGestione]   = useState(false);
  const [selectedDate, setSelectedDate]   = useState("");
  const [selectedAutista, setSelectedAutista] = useState("");
  const [ordineDrag, setOrdineDrag]       = useState([]);
  const [showMap, setShowMap]             = useState(false);
  const [mapOrders, setMapOrders]         = useState([]);
  const [mapLoading, setMapLoading]       = useState(false);
  const geocodeCacheRef                   = useRef(new Map());
  const NO_DRIVER_COLOR                   = "#ef4444";
  const DEFAULT_PIN                       = "#6366f1";
  const [notFound, setNotFound]           = useState([]);
  const [mapFilter, setMapFilter]         = useState("ALL");
  const [sidebarOpen, setSidebarOpen]     = useState(true);
  const [mapResizeTick, setMapResizeTick] = useState(0);

  useEffect(() => { fetchAutisti(); }, []);

  const fetchAutisti = async () => {
    try {
      const res = await axios.get("/api/autisti");
      setAutisti(res.data);
    } catch {
      toast.error("Errore nel caricamento degli autisti");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const autista = { nome, veicolo, colore };
    try {
      if (editingNome) {
        await axios.put(`/api/autisti/${editingNome}`, autista);
        toast.success("Autista aggiornato");
      } else {
        await axios.post("/api/addAutista", autista);
        toast.success("Autista aggiunto");
      }
      setNome(""); setVeicolo(""); setColore("#6366f1"); setEditingNome(null);
      fetchAutisti();
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleEdit = (a) => {
    setNome(a.nome); setVeicolo(a.veicolo); setColore(a.colore); setEditingNome(a.nome);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (n) => {
    if (!window.confirm("Vuoi davvero eliminare questo autista?")) return;
    try {
      await axios.delete(`/api/autisti/${n}`);
      toast.success("Autista eliminato");
      fetchAutisti();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const cancelEdit = () => {
    setNome(""); setVeicolo(""); setColore("#6366f1"); setEditingNome(null);
  };

  // ─── Gestione consegne ───────────────────────────────────────────────────────
  const estraiClienteDaOrderId = (orderId) => {
    if (!orderId) return "";
    const lastDash = orderId.lastIndexOf(" - ");
    return lastDash === -1 ? orderId : orderId.substring(0, lastDash).trim();
  };

  const fetchAndFilterOrders = async () => {
    try {
      const res = await axios.get("/api/orders");
      const normalizeDate = (d) => d?.split("T")[0];
      const filtrati = res.data.filter(
        (o) => o.autista === selectedAutista && normalizeDate(o.orderDate) === selectedDate
      );
      if (!filtrati.length) {
        toast.warn("Nessun ordine trovato per i criteri selezionati");
        setOrdineDrag([]);
        return;
      }
      setOrdineDrag(filtrati.map((o, idx) => ({
        id: `${idx}`,
        cliente: estraiClienteDaOrderId(o.orderId),
        sede: o.sede || "Sede non specificata",
      })));
    } catch {
      toast.error("Errore nel recupero ordini");
    }
  };

  const scaricaPDF = () => {
    const doc = new jsPDF();
    doc.text(`Consegne di ${selectedAutista} del ${selectedDate}`, 14, 10);
    doc.autoTable({
      head: [["Cliente", "Sede"]],
      body: ordineDrag.map((o) => [o.cliente, o.sede]),
      startY: 20,
    });
    doc.save(`consegne_${selectedAutista}_${selectedDate}.pdf`);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(ordineDrag);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    setOrdineDrag(items);
  };

  // ─── Mappa ───────────────────────────────────────────────────────────────────
  function normalizeAddress(raw) {
    if (!raw) return "";
    let a = String(raw).trim().replace(/\s*\(([^)]+)\)\s*$/u, ", $1");
    if (!/italy|italia/i.test(a)) a = `${a}, Italy`;
    return a;
  }

  function isValidAddress(s) {
    if (!s) return false;
    const t = String(s).trim();
    return !(!t || /^sede non specificata/i.test(t) || t.length <= 3);
  }

  function getAutistaColor(name) {
    const a = autisti.find((x) => x.nome === name);
    return a?.colore || NO_DRIVER_COLOR;
  }

  async function geocodeAddress(addr) {
    const key = addr.toLowerCase();
    if (geocodeCacheRef.current.has(key)) return geocodeCacheRef.current.get(key);
    const res = await axios.get("/api/geocode", { params: { addr } });
    const { lat, lng } = res.data || {};
    if (typeof lat === "number" && typeof lng === "number") {
      const val = { lat, lng };
      geocodeCacheRef.current.set(key, val);
      return val;
    }
    throw new Error("Geocoding fallito per " + addr);
  }

  async function fetchOrdersByDateAll(dateStr) {
    const res = await axios.get("/api/orders");
    const normalizeDate = (d) => d?.split("T")[0];
    return (res.data || [])
      .filter((o) => normalizeDate(o.orderDate) === dateStr)
      .map((o, idx) => ({
        id: o.id ?? idx,
        orderId: o.orderId,
        cliente: estraiClienteDaOrderId(o.orderId),
        sede: o.sede || "Sede non specificata",
        autista: o.autista || "",
      }));
  }

  async function openMapForDay() {
    if (!selectedDate) { toast.warn("Seleziona una data prima di aprire la mappa."); return; }
    try {
      setMapLoading(true);
      setNotFound([]);
      const ordini = await fetchOrdersByDateAll(selectedDate);
      const distinct = Array.from(new Set(ordini.map((o) => normalizeAddress(o.sede)).filter(isValidAddress)));
      for (const addr of distinct) {
        try { await geocodeAddress(addr); } catch (e) { console.warn("Geocode KO:", addr, e.message); }
      }
      const enriched = [], nf = [];
      for (const o of ordini) {
        const addr = normalizeAddress(o.sede);
        const g = geocodeCacheRef.current.get(addr.toLowerCase());
        if (g?.lat && g?.lng) {
          enriched.push({ ...o, lat: g.lat, lng: g.lng, color: o.autista ? getAutistaColor(o.autista) : NO_DRIVER_COLOR });
        } else {
          nf.push({ orderId: o.orderId, sede: o.sede || "" });
        }
      }
      if (!enriched.length) { setNotFound(nf); toast.warn("Nessun indirizzo geocodificato."); return; }
      setMapOrders(enriched);
      setNotFound(nf);
      setShowMap(true);
    } catch (err) {
      console.error(err);
      toast.error("Errore durante l'apertura della mappa.");
    } finally {
      setMapLoading(false);
    }
  }

  async function assignAutista(orderId, nuovoAutista) {
    try {
      const payload = { orderId, autista: (nuovoAutista && nuovoAutista.trim()) ? nuovoAutista.trim() : null };
      await axios.post("/api/modifydriver", payload);
      setMapOrders((prev) =>
        prev.map((o) =>
          o.orderId === orderId
            ? { ...o, autista: payload.autista || "", color: payload.autista ? getAutistaColor(payload.autista) : DEFAULT_PIN }
            : o
        )
      );
      toast.success(`Autista aggiornato: ${payload.autista || "Nessuno"}`);
    } catch {
      toast.error("Errore nell'assegnazione autista");
    }
  }

  // ─── LeafletMap ──────────────────────────────────────────────────────────────
  function LeafletMap({ orders, autisti: driverList, onAssign, filterAutista, resizeKey }) {
    const filtered = useMemo(() => {
      return orders.filter((o) => {
        if (filterAutista === "ALL") return true;
        if (filterAutista === "__NONE__") return !o.autista;
        return o.autista === filterAutista;
      });
    }, [orders, filterAutista]);

    const bounds = useMemo(() => {
      if (!filtered.length) return null;
      return L.latLngBounds(filtered.map((o) => L.latLng(o.lat, o.lng)));
    }, [filtered]);

    const mapRef = useRef(null);

    useEffect(() => {
      const map = mapRef.current;
      if (!map) return;
      const t = setTimeout(() => {
        try {
          const c = map.getContainer?.();
          if (!(c && c.offsetWidth && c.offsetHeight && c.offsetParent !== null)) return;
          if (bounds) map.fitBounds(bounds, { padding: [24, 24] });
          map.invalidateSize({ pan: false });
          setTimeout(() => { try { map.invalidateSize({ pan: false }); } catch {} }, 80);
        } catch {}
      }, 200);
      return () => clearTimeout(t);
    }, [bounds, filterAutista, resizeKey]);

    const [pendingAssign, setPendingAssign] = useState({});
    const [saving, setSaving] = useState({});

    return (
      <MapContainer
        key={resizeKey}
        whenCreated={(map) => { mapRef.current = map; }}
        center={[45.4642, 9.19]}
        zoom={11}
        style={{ width: "100%", height: "100%" }}
        scrollWheelZoom
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
          maxZoom={19}
          updateWhenZooming
          keepBuffer={6}
        />
        {filtered.map((order) => (
          <CircleMarker
            key={order.orderId}
            center={[order.lat, order.lng]}
            radius={8}
            weight={2}
            color={order.color || "#333"}
            fillColor={order.color || "#333"}
            fillOpacity={0.85}
          >
            <Popup>
              <div style={{ minWidth: 220, fontFamily: "'Inter','Segoe UI',sans-serif" }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{order.cliente}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>{order.sede}</div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 5 }}>
                  Assegna autista
                </label>
                <select
                  value={pendingAssign[order.orderId] ?? order.autista ?? ""}
                  onChange={(e) => setPendingAssign((p) => ({ ...p, [order.orderId]: e.target.value }))}
                  style={{ width: "100%", padding: "6px 8px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13, fontFamily: "inherit" }}
                  disabled={!!saving[order.orderId]}
                >
                  <option value="">— Nessuno —</option>
                  {driverList.map((a) => (
                    <option key={a.nome} value={a.nome}>{a.nome}</option>
                  ))}
                </select>
                <button
                  onClick={async () => {
                    const chosen = pendingAssign[order.orderId] ?? order.autista ?? "";
                    setSaving((s) => ({ ...s, [order.orderId]: true }));
                    await onAssign(order.orderId, chosen || null);
                    setSaving((s) => ({ ...s, [order.orderId]: false }));
                  }}
                  disabled={!!saving[order.orderId]}
                  style={{
                    marginTop: 8, width: "100%",
                    background: saving[order.orderId] ? "#94a3b8" : "#6366f1",
                    color: "#fff", border: "none", borderRadius: 8,
                    padding: "7px 10px", cursor: saving[order.orderId] ? "not-allowed" : "pointer",
                    fontWeight: 600, fontSize: 13, fontFamily: "inherit",
                  }}
                >
                  {saving[order.orderId] ? "Salvo..." : "Salva"}
                </button>
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <GlobalStyle />
      <Fonts />
      <Page>
        <PageHeader>
          <PageTitle>
            <span style={{ fontSize: 24 }}>🚗</span>
            Gestione Autisti
          </PageTitle>
          <Btn variant="primary" onClick={() => setShowGestione(true)}>
            <FaTruck size={13} />
            Gestisci consegne
          </Btn>
        </PageHeader>

        <Body>
          {/* ─── Form card ─────────────────────────────────────────── */}
          <Card>
            <CardTitle>
              {editingNome ? <FaEdit size={14} /> : <FaPlus size={14} />}
              {editingNome ? `Modifica ${editingNome}` : "Nuovo autista"}
            </CardTitle>
            <form onSubmit={handleSubmit}>
              <FieldGroup>
                <div>
                  <Label htmlFor="nome">Nome</Label>
                  <Input
                    id="nome"
                    type="text"
                    placeholder="Es. Mario Rossi"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="veicolo">Veicolo</Label>
                  <Input
                    id="veicolo"
                    type="text"
                    placeholder="Es. Fiat Ducato AB123CD"
                    value={veicolo}
                    onChange={(e) => setVeicolo(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Colore identificativo</Label>
                  <ColorRow>
                    <ColorPreview color={colore} />
                    <ColorInput
                      type="color"
                      value={colore}
                      onChange={(e) => setColore(e.target.value)}
                    />
                    <ColorHint>Usato per pin e mappa</ColorHint>
                  </ColorRow>
                </div>
              </FieldGroup>
              <FormActions>
                <Btn variant="primary" type="submit" style={{ flex: 1, justifyContent: "center" }}>
                  {editingNome ? "Aggiorna" : "Aggiungi"}
                </Btn>
                {editingNome && (
                  <Btn variant="ghost" type="button" onClick={cancelEdit}>
                    Annulla
                  </Btn>
                )}
              </FormActions>
            </form>
          </Card>

          {/* ─── Driver list ───────────────────────────────────────── */}
          <Card>
            <CardTitle>
              <span>👥</span>
              Autisti registrati
              <span style={{ marginLeft: "auto", fontWeight: 400, fontSize: 13, color: C.muted }}>
                {autisti.length} {autisti.length === 1 ? "autista" : "autisti"}
              </span>
            </CardTitle>
            {autisti.length === 0 ? (
              <EmptyState>Nessun autista registrato.<br />Aggiungine uno con il form a sinistra.</EmptyState>
            ) : (
              <DriverList>
                {autisti.map((a) => (
                  <DriverCard key={a.nome} editing={editingNome === a.nome ? 1 : 0}>
                    <DriverAvatar color={a.colore}>
                      {a.nome.charAt(0).toUpperCase()}
                    </DriverAvatar>
                    <DriverInfo>
                      <DriverName>{a.nome}</DriverName>
                      <DriverVehicle>
                        <FaTruck size={10} />
                        {a.veicolo || "—"}
                      </DriverVehicle>
                    </DriverInfo>
                    <DriverActions>
                      <IconBtn variant="edit" title="Modifica" onClick={() => handleEdit(a)}>
                        <FaEdit />
                      </IconBtn>
                      <IconBtn variant="delete" title="Elimina" onClick={() => handleDelete(a.nome)}>
                        <FaTrash />
                      </IconBtn>
                    </DriverActions>
                  </DriverCard>
                ))}
              </DriverList>
            )}
          </Card>
        </Body>

        <ToastContainer position="bottom-right" autoClose={3000} />

        {/* ─── Modale gestione consegne ─────────────────────────────────────── */}
        <ReactModal
          isOpen={showGestione}
          onRequestClose={() => setShowGestione(false)}
          style={{ overlay: modalOverlay, content: buildModalContent("600px") }}
        >
          <ModalTitle>
            <FaTruck size={16} color={C.primary} />
            Gestisci consegne
          </ModalTitle>

          <FormRow>
            <FormLabel>Data</FormLabel>
            <ModalInput
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </FormRow>
          <FormRow>
            <FormLabel>Autista</FormLabel>
            <ModalSelect
              value={selectedAutista}
              onChange={(e) => setSelectedAutista(e.target.value)}
            >
              <option value="">— Seleziona —</option>
              {autisti.map((a) => (
                <option key={a.nome} value={a.nome}>{a.nome}</option>
              ))}
            </ModalSelect>
          </FormRow>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
            <Btn variant="primary" onClick={fetchAndFilterOrders}>
              <FaSearch size={12} />
              Cerca consegne
            </Btn>
            <Btn
              variant="default"
              onClick={openMapForDay}
              disabled={!selectedDate || mapLoading}
            >
              <FaMapMarkedAlt size={13} />
              {mapLoading ? "Caricamento..." : "Apri mappa"}
            </Btn>
          </div>

          {ordineDrag.length > 0 && (
            <>
              <div style={{ marginTop: 24, marginBottom: 10, fontWeight: 600, fontSize: 14, color: C.text }}>
                Ordina le consegne — trascina per riordinare
              </div>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="lista">
                  {(provided) => (
                    <ul
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{ listStyle: "none", padding: 0, maxHeight: 300, overflowY: "auto" }}
                    >
                      {ordineDrag.map((item, index) => (
                        <Draggable key={item.id} draggableId={item.id} index={index}>
                          {(provided) => (
                            <DragItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{ ...provided.draggableProps.style }}
                            >
                              <span style={{ color: C.muted, fontSize: 16, userSelect: "none" }}>⠿</span>
                              <div>
                                <span style={{ fontWeight: 600 }}>{item.cliente}</span>
                                <span style={{ color: C.muted, marginLeft: 8, fontSize: 13 }}>{item.sede}</span>
                              </div>
                            </DragItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          )}

          <ModalActions>
            {ordineDrag.length > 0 && (
              <Btn variant="success" onClick={scaricaPDF}>
                <FaFilePdf size={13} />
                Scarica PDF
              </Btn>
            )}
            <Btn variant="ghost" onClick={() => setShowGestione(false)}>
              Chiudi
            </Btn>
          </ModalActions>
        </ReactModal>

        {/* ─── Modale mappa (fullscreen) ────────────────────────────────────── */}
        <ReactModal
          isOpen={showMap}
          onAfterOpen={() => setMapResizeTick((t) => t + 1)}
          onRequestClose={() => setShowMap(false)}
          style={{
            content: {
              top: 0, left: 0, right: 0, bottom: 0,
              padding: 0, margin: 0, borderRadius: 0,
              width: "100vw", height: "100vh", border: "none",
            },
            overlay: { backgroundColor: "rgba(15,23,42,0.5)", zIndex: 1000 },
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: "'Inter','Segoe UI',sans-serif" }}>
            {/* Header */}
            <div style={{
              padding: "12px 20px",
              borderBottom: `1px solid ${C.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: C.card,
              flex: "0 0 auto",
              boxShadow: C.shadow,
            }}>
              <div style={{ fontWeight: 700, fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                <FaMapMarkedAlt color={C.primary} />
                Mappa consegne — {selectedDate}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => { setSidebarOpen((s) => !s); setMapResizeTick((t) => t + 1); }}
                  title={sidebarOpen ? "Nascondi legenda" : "Mostra legenda"}
                  style={{
                    width: 36, height: 36, borderRadius: 8,
                    background: C.primaryLight, color: C.primary,
                    border: "none", cursor: "pointer",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  {sidebarOpen ? <FaTimes /> : <FaBars />}
                </button>
                <button
                  onClick={() => setShowMap(false)}
                  style={{
                    height: 36, padding: "0 16px", borderRadius: 8,
                    background: C.bg, color: C.textSec,
                    border: `1.5px solid ${C.border}`, cursor: "pointer",
                    fontWeight: 600, fontSize: 13, fontFamily: "inherit",
                  }}
                >
                  Chiudi
                </button>
              </div>
            </div>

            {/* Body */}
            <div style={{ display: "flex", flex: "1 1 auto", minHeight: 0, minWidth: 0, overflow: "hidden" }}>
              <div style={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
                <LeafletMap
                  orders={mapOrders}
                  autisti={autisti}
                  onAssign={assignAutista}
                  filterAutista={mapFilter}
                  resizeKey={`${sidebarOpen ? "open" : "closed"}-${mapResizeTick}`}
                />
              </div>

              {sidebarOpen && (
                <aside style={{
                  flex: "0 0 290px",
                  width: 290,
                  borderLeft: `1px solid ${C.border}`,
                  padding: 20,
                  overflowY: "auto",
                  background: C.card,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.textSec, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                    Filtro autista
                  </div>
                  <ModalSelect
                    value={mapFilter}
                    onChange={(e) => { setMapFilter(e.target.value); setMapResizeTick((t) => t + 1); }}
                    style={{ flex: "unset", width: "100%", marginBottom: 20 }}
                  >
                    <option value="ALL">Tutti</option>
                    <option value="__NONE__">— Senza autista —</option>
                    {autisti.map((a) => (
                      <option key={a.nome} value={a.nome}>{a.nome}</option>
                    ))}
                  </ModalSelect>

                  <div style={{ fontSize: 12, fontWeight: 700, color: C.textSec, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>
                    Legenda
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                    {autisti.map((a) => (
                      <div key={a.nome} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{
                          width: 12, height: 12, borderRadius: "50%",
                          background: a.colore, border: `2px solid ${C.border}`,
                          display: "inline-block", flexShrink: 0,
                        }} />
                        <span style={{ fontSize: 13, color: C.text }}>{a.nome}</span>
                      </div>
                    ))}
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{
                        width: 12, height: 12, borderRadius: "50%",
                        background: NO_DRIVER_COLOR, border: `2px solid ${C.border}`,
                        display: "inline-block", flexShrink: 0,
                      }} />
                      <span style={{ fontSize: 13, color: C.text }}>Senza autista</span>
                    </div>
                  </div>

                  <div style={{ fontSize: 12, fontWeight: 700, color: C.textSec, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                    Indirizzi non trovati
                  </div>
                  {notFound.length === 0 ? (
                    <div style={{ fontSize: 13, color: C.success }}>✓ Nessuno</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {notFound.map((x, i) => (
                        <div key={`${x.orderId}-${i}`} style={{
                          fontSize: 12, color: C.textSec,
                          background: C.bg, borderRadius: 8,
                          padding: "6px 10px", border: `1px solid ${C.border}`,
                        }}>
                          <span style={{ color: C.muted }}>"{x.sede}"</span>
                          <br />
                          <strong style={{ color: C.text }}>{x.orderId}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </aside>
              )}
            </div>
          </div>
        </ReactModal>
      </Page>
    </>
  );
}

export default Autisti;
